import { subDays, format, startOfWeek, addDays } from 'date-fns';
import { useState, useRef, useEffect } from 'react';

import { DailyMood } from '../stores/useDailyMoodStore';

import { useDateLocale } from '@/common/hooks/useDateLocale';

interface MoodCalendarProps {
  moods: DailyMood[];
}

export default function MoodCalendar({ moods }: MoodCalendarProps) {
  const { locale } = useDateLocale();
  const [selectedMood, setSelectedMood] = useState<{ mood: DailyMood; date: Date } | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  const startDate = subDays(today, 28);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setSelectedMood(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Create week rows
  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  const firstDayOfCalendar = startOfWeek(startDate);
  let currentDate = firstDayOfCalendar;

  while (currentDate <= today) {
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  const getMoodForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    // Don't show future dates
    if (dateStr > todayStr) return undefined;

    return moods.find(mood => mood.date === dateStr);
  };

  const getBackgroundColor = (mood?: DailyMood) => {
    if (!mood) return 'bg-gray-100';
    return 'bg-slate-200';
  };

  const weekDays = [0, 1, 2, 3, 4, 5, 6].map((day) =>
    format(new Date(2024, 0, 7 + day), 'EEE', { locale })
  );

  const handleMoodClick = (mood: DailyMood, date: Date) => {
    if (mood.note) {
      setSelectedMood({ mood, date });
    }
  };

  return (
    <div className="overflow-x-auto relative">
      <div className="flex gap-2">
        {/* Week days labels */}
        <div className="flex flex-col gap-2 pr-2">
          {weekDays.map(day => (
            <div key={day} className="h-8 text-xs text-gray-500 flex items-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex flex-col gap-2">
            {week.map((day, dayIndex) => {
              const mood = getMoodForDay(day);
              return (
                <div
                  key={dayIndex}
                  className={`w-8 h-8 rounded-lg ${getBackgroundColor(mood)} 
                    transition-colors hover:opacity-75 flex items-center justify-center
                    relative ${mood?.note ? 'cursor-pointer' : ''}`}
                  title={format(day, 'MMM d', { locale })}
                  onClick={() => mood && handleMoodClick(mood, day)}
                >
                  {mood && (
                    <span className="text-xl">
                      {mood.emoji}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Note popup */}
      {selectedMood && (
        <div
          ref={popupRef}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
            bg-white rounded-xl shadow-lg p-4 w-64 z-20"
        >
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm text-gray-500">
              {format(selectedMood.date, 'MMM d, yyyy', { locale })}
            </div>
            <button
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setSelectedMood(null)}
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">{selectedMood.mood.emoji}</span>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {selectedMood.mood.note}
          </p>
        </div>
      )}
    </div>
  );
}
